import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  Collapse,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  groupChatHistoriesByDate,
  formatChatTimestamp,
} from "../../utils/chatHistoryHelper";
import { useRbac } from "../../hooks/RbacContext";

export const ChatHistorySidebar = ({
  chats = [],
  loading = false,
  loadingMore = false,
  currentChatId,
  onChatSelect,
  onNewChat,
  onChatDelete,
  onLoadMore,
  hasMore = false,
  onOpenConnection,
}) => {
  const [expandedGroups, setExpandedGroups] = useState({
    today: true,
    yesterday: true,
    week: true,
    older: true,
  });
  const [copyTooltip, setCopyTooltip] = useState(null); // { id, message } or null
  const { checkAccess } = useRbac();
  const canDeleteChat = checkAccess("askFlashdice", "deleteChat");

  const handleCopySessionId = async (e, chatId, sessionId) => {
    e.stopPropagation();
    if (!sessionId) return;
    try {
      await navigator.clipboard.writeText(String(sessionId));
      setCopyTooltip({ id: chatId, message: "Copied!" });
      setTimeout(() => setCopyTooltip(null), 1500);
    } catch (err) {
      setCopyTooltip({ id: chatId, message: "Failed to copy" });
      setTimeout(() => setCopyTooltip(null), 1500);
    }
  };

  const handleDelete = (e, chat) => {
    e.stopPropagation();
    onChatDelete?.(chat);
  };

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const scrollRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const fillCheckTimeoutRef = useRef(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !onLoadMore || !hasMore || loading || loadingMore || loadingMoreRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 80;
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadingMoreRef.current = true;
      onLoadMore();
      setTimeout(() => { loadingMoreRef.current = false; }, 500);
    }
  }, [onLoadMore, hasMore, loading, loadingMore]);

  // Keep calling pagination until the list section is fully filled (scrollbar appears) or no more data.
  const runFillCheck = useCallback(() => {
    if (!onLoadMore || !hasMore || loading || loadingMore) return;
    const el = scrollRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 4) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, loading, loadingMore]);

  useEffect(() => {
    if (!onLoadMore || !hasMore || loading || loadingMore) return;

    const runCheck = () => {
      runFillCheck();
      fillCheckTimeoutRef.current = setTimeout(runFillCheck, 120);
    };

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(runCheck);
    });
    return () => {
      cancelAnimationFrame(id);
      if (fillCheckTimeoutRef.current) {
        clearTimeout(fillCheckTimeoutRef.current);
        fillCheckTimeoutRef.current = null;
      }
    };
  }, [chats, hasMore, loading, loadingMore, onLoadMore, runFillCheck]);

  const groupedHistories = groupChatHistoriesByDate(
    Array.isArray(chats) ? chats : []
  );

  const renderLoadingSkeletons = (count = 5) => (
    <Box sx={{ px: 1.5, py: 1 }}>
      {Array.from({ length: count }, (_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            mb: 1.25,
          }}
        >
          <Skeleton
            variant="text"
            width={i === 0 ? "75%" : i % 2 === 0 ? "90%" : "70%"}
            height={14}
            animation="wave"
            sx={{ bgcolor: "grey.200", flexShrink: 0 }}
          />
          <Skeleton
            variant="text"
            width="45%"
            height={10}
            animation="wave"
            sx={{ bgcolor: "grey.100" }}
          />
        </Box>
      ))}
    </Box>
  );

  const renderGroup = (title, chats, groupName) => {
    if (!chats.length) return null;

    return (
      <Box key={groupName}>
        {/* Group Header */}
        <ListItemButton
          onClick={() => toggleGroup(groupName)}
          sx={{
            py: 0.25,
            px: 1,
            minHeight: 24,
          }}
        >
          {expandedGroups[groupName] ? (
            <ExpandMoreIcon sx={{ fontSize: 14, mr: 0.5, color: "#64748b" }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 14, mr: 0.5, color: "#64748b" }} />
          )}
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 600,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            {title}
          </Typography>
        </ListItemButton>

        {/* Chats */}
        <Collapse in={expandedGroups[groupName]} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {chats.map((chat) => (
              <ListItem
                key={chat.id}
                disablePadding
                sx={{
                  "&:hover .delete-btn": { opacity: 1 },
                  "&:hover .copy-btn": { opacity: 1 },
                }}
              >
                <ListItemButton
                  selected={currentChatId === chat.id}
                  onClick={() => onChatSelect(chat)}
                  sx={{
                    py: 0.25,
                    px: 1,
                    minHeight: 32,
                    mx: 0.5,
                    borderRadius: 1,
                    flexDirection: "column",
                    alignItems: "stretch",
                    "&.Mui-selected": {
                      backgroundColor: "#f1f5f9",
                      "&:hover": { backgroundColor: "#e2e8f0" },
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight:
                              currentChatId === chat.id ? 500 : 400,
                            color: "#1e293b",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.2,
                          }}
                        >
                          {chat.title || "New Chat"}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            color: "#94a3b8",
                            lineHeight: 1.1,
                          }}
                        >
                          {formatChatTimestamp(chat.timestamp)}
                        </Typography>
                      }
                      sx={{ flex: 1, minWidth: 0 }}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
                      {/* Session ID copy temporarily disabled */}
                      {/* {chat.session_id && (
                        <Tooltip title={copyTooltip?.id === chat.id ? copyTooltip.message : "Copy session ID"}>
                          <IconButton
                            size="small"
                            className="copy-btn"
                            onClick={(e) => handleCopySessionId(e, chat.id, chat.session_id)}
                            sx={{
                              opacity: 0,
                              p: "2px",
                              color: "#94a3b8",
                              transition: "opacity 0.2s",
                              "&:hover": {
                                color: "#3F49A7",
                                backgroundColor: "#f1f5f9",
                              },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )} */}
                      {canDeleteChat && (
                        <Tooltip title="Delete chat">
                          <IconButton
                            size="small"
                            className="delete-btn"
                            onClick={(e) => handleDelete(e, chat)}
                            sx={{
                              opacity: 0,
                              p: "2px",
                              color: "#94a3b8",
                              transition: "opacity 0.2s",
                              "&:hover": {
                                color: "#dc2626",
                                backgroundColor: "#fee2e2",
                              },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: 250,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        borderRight: "1px solid #e2e8f0",
      }}
    >
      <Box
        sx={{
          pl: 1,
          pr: 1.5,
          py: 0.75,
          minHeight: 36,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
          Ask FlashDice
        </Typography>
        <Tooltip title="New Chat">
          <IconButton
            size="small"
            onClick={onNewChat}
            sx={{
              color: "#3F49A7",
              backgroundColor: "#f1f5f9",
              "&:hover": { backgroundColor: "#e2e8f0" },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chat List */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: "#cbd5e1",
            borderRadius: 3,
          },
        }}
      >
        {loading && !chats?.length ? (
          renderLoadingSkeletons(6)
        ) : !chats?.length ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
              No chat history yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 0.5 }}>
            {renderGroup("Today", groupedHistories.today, "today")}
            {renderGroup("Yesterday", groupedHistories.yesterday, "yesterday")}
            {renderGroup("Previous 7 days", groupedHistories.week, "week")}
            {renderGroup("Older", groupedHistories.older, "older")}
            {loadingMore && (
              <Box sx={{ mt: 0.5 }}>{renderLoadingSkeletons(3)}</Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};